import { supabase } from './supabase';
import type { HomeownerRecord, Activity, Property } from '../types';
import type { PipelineStage } from '../constants';

export interface HomeownerWithDetails extends HomeownerRecord {
  property: Property;
  last_activity: Activity | null;
}

export async function listHomeowners(agentId: string): Promise<HomeownerWithDetails[]> {
  const { data, error } = await supabase
    .from('doorsteps_homeowner_records')
    .select('*, property:doorsteps_properties(*)')
    .eq('agent_id', agentId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  const records = (data || []) as any[];

  if (!records.length) return [];

  // Hydrate last activity for each record in one batch.
  const ids = records.map((r) => r.id);
  const { data: acts } = await supabase
    .from('doorsteps_activities')
    .select('*')
    .in('homeowner_record_id', ids)
    .order('created_at', { ascending: false });
  const lastByRecord = new Map<string, Activity>();
  for (const a of (acts || []) as Activity[]) {
    if (!lastByRecord.has(a.homeowner_record_id)) lastByRecord.set(a.homeowner_record_id, a);
  }

  return records.map((r) => ({
    ...r,
    last_activity: lastByRecord.get(r.id) ?? null,
  })) as HomeownerWithDetails[];
}

export async function getActivities(homeownerId: string): Promise<Activity[]> {
  const { data, error } = await supabase
    .from('doorsteps_activities')
    .select('*')
    .eq('homeowner_record_id', homeownerId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Activity[];
}

export async function setPipelineStage(recordId: string, stage: PipelineStage): Promise<void> {
  const { error } = await supabase.rpc('doorsteps_set_pipeline_stage', {
    p_record_id: recordId,
    p_stage: stage,
  });
  if (error) throw error;
}

export async function addNote(recordId: string, agentId: string, text: string): Promise<void> {
  await supabase
    .from('doorsteps_homeowner_records')
    .update({ notes: text, updated_at: new Date().toISOString() })
    .eq('id', recordId);
  await supabase
    .from('doorsteps_activities')
    .insert({
      homeowner_record_id: recordId,
      agent_id: agentId,
      type: 'note_added',
      payload: { text },
    });
}

export async function logCall(recordId: string, agentId: string, summary: string): Promise<void> {
  await supabase
    .from('doorsteps_activities')
    .insert({
      homeowner_record_id: recordId,
      agent_id: agentId,
      type: 'call_logged',
      payload: { summary },
    });
}
